import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import logoMark from "../assets/releaf_mark.png";

const input = "mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm outline-none transition-colors focus:border-gold";
const label = "text-xs font-semibold uppercase tracking-[0.2em] text-moss";
const btn = "w-full rounded-md bg-forest px-6 py-3.5 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark disabled:opacity-50";

function RequestForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  if (sent) return (
    <div data-testid="reset-request-sent" className="rounded-md border border-forest/30 bg-forest/10 px-4 py-4 text-sm text-forest">
      If an account exists for <strong>{email}</strong>, a reset link is on its way. Check your inbox (and spam folder) — the link works for 1 hour.
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-5">
      <div>
        <label htmlFor="reset-email" className={label}>Email</label>
        <input id="reset-email" data-testid="reset-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} autoComplete="email" />
      </div>
      {error && <p data-testid="reset-error" className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}
      <button type="submit" data-testid="reset-request-submit" disabled={busy} className={btn}>{busy ? "Sending..." : "Email Me a Reset Link"}</button>
    </form>
  );
}

function NewPasswordForm({ token }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords don't match.");
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  if (done) return <div data-testid="reset-success" className="rounded-md border border-forest/30 bg-forest/10 px-4 py-4 text-sm text-forest">Password updated. Taking you to sign in...</div>;

  return (
    <form onSubmit={submit} className="space-y-5">
      <div>
        <label htmlFor="new-password" className={label}>New password</label>
        <input id="new-password" data-testid="reset-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={input} autoComplete="new-password" />
        <p className="mt-1 text-xs text-moss">At least 8 characters.</p>
      </div>
      <div>
        <label htmlFor="confirm-password" className={label}>Confirm password</label>
        <input id="confirm-password" data-testid="reset-confirm-input" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={input} autoComplete="new-password" />
      </div>
      {error && <p data-testid="reset-error" className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}
      <button type="submit" data-testid="reset-password-submit" disabled={busy} className={btn}>{busy ? "Saving..." : "Save New Password"}</button>
    </form>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");

  return (
    <main data-testid="reset-password-page" className="flex min-h-screen items-center justify-center bg-ivory px-6 py-16">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-3">
          <img src={logoMark} alt="Releaf monogram" className="h-12 w-auto" />
          <span className="block font-serif text-xl text-forest">Releaf</span>
        </Link>
        <h1 className="mt-10 font-serif text-3xl text-forest">{token ? "Choose a new password" : "Forgot your password?"}</h1>
        <p className="mt-2 text-sm text-moss">{token ? "Enter a new password for your account." : "Enter your email and we'll send you a secure link to reset it."}</p>
        <div className="mt-8">{token ? <NewPasswordForm token={token} /> : <RequestForm />}</div>
        <div className="mt-8 flex gap-5 text-sm font-medium text-moss">
          <Link to="/admin/login" data-testid="reset-back-admin" className="transition-colors hover:text-gold-dark">Provider sign in</Link>
          <Link to="/portal/login" data-testid="reset-back-portal" className="transition-colors hover:text-gold-dark">Patient sign in</Link>
        </div>
      </div>
    </main>
  );
}
